/**
 * Date formatting utilities for SideoutIQ.
 *
 * Uses the browser's navigator.language so dates render in the user's
 * local format automatically (e.g. DD/MM/YYYY for UK, MM/DD/YYYY for US).
 *
 * Always pass a Date object or an ISO date string (YYYY-MM-DD).
 * Never pass ambiguous strings like "4/5/2025" — they parse differently
 * across locales. Use robustParseDate() from Schedule.tsx if you have
 * a mixed-format string.
 */

type DateInput = Date | string;

function toDate(input: DateInput): Date | null {
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  if (!input) return null;
  // ISO date-only strings (YYYY-MM-DD) must be parsed as local time,
  // not UTC, to avoid day-off-by-one errors.
  const isoMatch = input.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    const d = new Date(
      parseInt(isoMatch[1]),
      parseInt(isoMatch[2]) - 1,
      parseInt(isoMatch[3])
    );
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
}

function userLocale(): string {
  return (typeof navigator !== 'undefined' && navigator.language) || 'en-US';
}

/**
 * Format a date using the user's browser locale.
 * Falls back to '—' for invalid/missing dates.
 *
 * @example
 *   formatDate('2025-04-15', { month: 'short', day: 'numeric' })
 *   → "Apr 15"  (en-US)  |  "15 Apr"  (en-GB)
 */
export function formatDate(
  input: DateInput | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }
): string {
  if (!input) return '—';
  const d = toDate(input);
  if (!d) return '—';
  return d.toLocaleDateString(userLocale(), options);
}

/**
 * Format month + day only (no year).
 * @example "Apr 15"  (en-US)  |  "15 Apr"  (en-GB)
 */
export function formatMonthDay(input: DateInput | null | undefined): string {
  return formatDate(input, { month: 'short', day: 'numeric' });
}

/**
 * Format month abbreviation only.
 * @example "Apr"
 */
export function formatMonth(input: DateInput | null | undefined): string {
  return formatDate(input, { month: 'short' });
}

/**
 * Format a full date with weekday.
 * @example "Tue, Apr 15, 2025"  (en-US)  |  "Tue 15 Apr 2025"  (en-GB)
 */
export function formatDateWithWeekday(input: DateInput | null | undefined): string {
  return formatDate(input, { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * Format a date+time from separate date string and time string.
 * @example formatDateTime('2025-04-15', '18:30') → "Apr 15, 2025 @ 6:30 PM"
 */
export function formatDateTime(
  dateInput: DateInput | null | undefined,
  time?: string
): string {
  const datePart = formatDate(dateInput);
  if (!time) return datePart;
  return `${datePart} @ ${time}`;
}
